import {
  Grid2,
  PaymentFailedDialog,
  PaymentSuccessDowngradeDialog,
  PaymentSuccessUpgradeDialog,
} from '@aibees/shared/components';
import { PlanCard, PlanCardSkeleton } from '@aibees/shared/features';
import { useResponsive } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import { Box, Card, CardContent } from '@mui/material';
import { memo } from 'react';
import { productFamilyKey } from './catalog';
import { usePlanConfigs } from './hooks/use-plan-configs';
import { usePlanList } from './hooks/use-plan-list';
import * as SX from './sx';
import type { PricingCardsProps as Props } from './types';

const SKELETON_PLACEHOLDERS = ['free', 'starter', 'builder', 'scale'];

export const PricingCards = memo(({ className, yearly }: Props) => {
  const { productCards, isLoading } = usePlanList(yearly);
  const { byId, byFamily } = usePlanConfigs();
  const isSmall = useResponsive('down', 'sm');

  const showSkeleton = isLoading && productCards.length === 0;

  return (
    <Card
      className={classnames('PricingCards', className)}
      sx={SX.root}
      variant="filled"
      color="default"
    >
      <CardContent padding="extra" sx={SX.cardContent}>
        <Grid2 container spacing={1} columns={48}>
          <PaymentSuccessDowngradeDialog />
          <PaymentSuccessUpgradeDialog />
          <PaymentFailedDialog />
          {showSkeleton
            ? SKELETON_PLACEHOLDERS.map((slug) => (
                <Grid2
                  key={slug}
                  xs={48}
                  sm={isSmall ? 48 : 24}
                  md={24}
                  lg={12}
                  xl={12}
                >
                  <Box sx={SX.boxPlan}>
                    <PlanCardSkeleton isMostPopular={slug === 'builder'} />
                  </Box>
                </Grid2>
              ))
            : productCards.map(({ product, plans, isFree }) => {
                // `product` comes from the entitlement `return=features` shape
                // while the configs are keyed off `return=cards` — resolve by
                // id, then fall back to the family/slug the backend groups on.
                // No Free-config fallback: it would render "Always free" and a
                // "Get Started" CTA on a paid card.
                const family = productFamilyKey(product);
                const config =
                  byId[product.id] ?? (family ? byFamily[family] : undefined);

                return (
                  <Grid2
                    key={product.id}
                    xs={48}
                    sm={isSmall ? 48 : 24}
                    md={24}
                    lg={12}
                    xl={12}
                  >
                    <Box sx={SX.boxPlan}>
                      <PlanCard
                        plans={plans}
                        yearly={yearly}
                        isFree={isFree}
                        config={config}
                        isLoading={isLoading}
                        isMostPopular={
                          product.metadata?.label === 'MOST_POPULAR'
                        }
                      />
                    </Box>
                  </Grid2>
                );
              })}
        </Grid2>
      </CardContent>
    </Card>
  );
});

PricingCards.displayName = 'PricingCards';
